import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Camera, Edit2, Filter, Plus, Trash2, X } from "lucide-react";
import Select from "react-select";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import { api, url } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const emptyForm = {
  nome: "",
  capacidade: "",
  status: "disponivel",
  servico_id: "",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Quartos = () => {
  const user = useSelector((state) => state.user.user);
  const [quartos, setQuartos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [foto, setFoto] = useState(null);
  const [filterServico, setFilterServico] = useState(null);

  const fetchServicos = async () => {
    try {
      const response = await api.get("/servicos");
      const allServicos = response.data?.data || response.data || [];
      const companyServicos = allServicos.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setServicos(companyServicos);
    } catch (err) {
      console.error("Erro ao carregar serviços", err);
    }
  };

  const fetchQuartos = async (servicoId = null) => {
    try {
      setLoading(true);
      setError(null);
      let endpoint = "/quartos";
      if (servicoId) {
        endpoint = `/quartos/filter?servico_id=${servicoId}&empresa_id=${user?.empresa_id}`;
      }
      const response = await api.get(endpoint);
      const allQuartos = response.data?.data || response.data || [];
      const companyQuartos = allQuartos.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setQuartos(companyQuartos);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar quartos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchQuartos();
      fetchServicos();
    }
  }, [user?.empresa_id]);

  const handleFilter = () => {
    fetchQuartos(filterServico ? filterServico.value : null);
  };

  const clearFilter = () => {
    setFilterServico(null);
    fetchQuartos(null);
  };

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setForm((current) => ({
      ...current,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFoto(null);
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      nome: item.nome || "",
      capacidade: item.capacidade || "",
      status: item.status || "disponivel",
      servico_id: item.servico_id || "",
    });
    setFoto(null);
    showModal("quartoFormModal");
  };

  const payload = () => {
    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      data.append(key, value);
    });
    if (user?.empresa_id) {
      data.append("empresa_id", user.empresa_id);
    }
    if (foto) data.append("foto", foto);
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        await api.post(`/quartos/${editing.id}`, payload(), {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await notify("Quarto actualizado com sucesso.", "success");
      } else {
        await api.post("/quartos", payload(), {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await notify("Quarto cadastrado com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      setFoto(null);
      closeModal("quartoFormModal");
      fetchQuartos(filterServico ? filterServico.value : null);
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar o quarto.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir quarto",
      text: `Deseja excluir o quarto ${selected.nome}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/quartos/${selected.id}`);
      await notify("Quarto excluído com sucesso.", "success");
      setSelected(null);
      fetchQuartos(filterServico ? filterServico.value : null);
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir o quarto.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin";
  const servicoOptions = servicos.map((s) => ({ value: s.id, label: s.nome }));

  return (
    <ResourcePage
      title="Quartos"
      actions={(
        <>
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#quartoFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white mb-3">
        <div className="card-body">
          <div className="row align-items-end g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="filter_servico">Filtrar por Serviço</label>
              <Select
                id="filter_servico"
                options={servicoOptions}
                value={filterServico}
                onChange={setFilterServico}
                isClearable
                placeholder="Selecione um serviço..."
              />
            </div>
            <div className="col-md-auto d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={handleFilter}>
                <Filter size={16} />
                Filtrar
              </button>
              {filterServico && (
                <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={clearFilter}>
                  <X size={16} />
                  Limpar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem gerir quartos.</div>}

          <DataTable
            id="quartosDataTable"
            columns={["Foto", "Nome", "Capacidade", "Serviço", "Estado"]}
            rows={quartos}
            renderRow={(item) => {
              const servicoObj = servicos.find((s) => s.id == item.servico_id);
              return (
                <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                  <td>
                    {item.foto ? (
                      <img src={url + item.foto} alt={item.nome} className="rounded" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{item.nome}</td>
                  <td>{item.capacidade || "-"}</td>
                  <td>{servicoObj ? servicoObj.nome : "-"}</td>
                  <td><span className={`badge ${item.status === "disponivel" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>{item.status}</span></td>
                </tr>
              );
            }}
          />
        </div>
      </div>

      <AppModal
        id="quartoFormModal"
        title={editing ? "Editar quarto" : "Cadastrar quarto"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="quartoForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="quartoForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="nome">Nome</label>
              <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="capacidade">Capacidade</label>
              <input className="form-control" id="capacidade" name="capacidade" value={form.capacidade} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="servico_id">Serviço</label>
              <Select
                options={servicoOptions}
                value={servicoOptions.find((option) => option.value == form.servico_id) || null}
                onChange={(option) => handleSelectChange(option, "servico_id")}
                isClearable
                placeholder="Selecione um serviço..."
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="status">Estado</label>
              <select className="form-select" id="status" name="status" value={form.status} onChange={handleChange}>
                <option value="disponivel">Disponível</option>
                <option value="ocupado">Ocupado</option>
                <option value="manutencao">Manutenção</option>
              </select>
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label" htmlFor="foto">Foto</label>
              <div className="input-group">
                <span className="input-group-text"><Camera size={16} /></span>
                <input className="form-control" id="foto" name="foto" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setFoto(event.target.files?.[0] || null)} />
              </div>
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Quartos;
